<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
  >
    <div
      v-if="!hasConsented"
      class="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-primary-500 shadow-lg z-50"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div class="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <!-- Content -->
          <div class="flex-1">
            <div class="flex items-start gap-3">
              <!-- Cookie Icon -->
              <div class="flex-shrink-0 mt-0.5">
                <svg
                  class="w-6 h-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <div>
                <h3 id="cookie-banner-title" class="font-semibold text-lg mb-1 text-gray-900">
                  {{ isWhiteLabel ? `${shopName} values your privacy` : 'We value your privacy' }}
                </h3>
                <p id="cookie-banner-description" class="text-sm text-gray-600 leading-relaxed">
                  <template v-if="isWhiteLabel">
                    {{ shopName }} uses cookies to enhance your browsing experience, remember your
                    cart, and analyze traffic. Essential cookies are required for the site to function.
                    You can accept all or customize your preferences.
                  </template>
                  <template v-else>
                    We use cookies to enhance your browsing experience, serve personalized content,
                    and analyze our traffic. Essential cookies are required for the site to function.
                    You can choose to accept all cookies or customize your preferences.
                  </template>
                  <router-link
                    :to="learnMoreHref"
                    class="text-primary-600 hover:text-primary-700 font-medium underline inline-flex items-center gap-1"
                  >
                    Learn more
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </router-link>
                </p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full lg:w-auto">
            <button
              @click="handleRejectAll"
              class="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Reject All
            </button>
            <button
              @click="showPreferences"
              class="px-4 py-2.5 border border-primary-500 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Customize
            </button>
            <button
              @click="handleAcceptAll"
              class="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-primary-700 focus:ring-offset-2"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>

      <!-- Preferences Modal -->
      <CookiePreferencesModal
        v-if="showModal"
        @close="showModal = false"
        @save="handleSavePreferences"
      />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useCookieConsent, type CookiePreferences } from '@/composables/useCookieConsent'
import { useShopBrand } from '@/composables/useShopBrand'
import CookiePreferencesModal from './CookiePreferencesModal.vue'

const { hasConsented, acceptAll, rejectAll, loadPreferences } = useCookieConsent()
const { brand, isWhiteLabel } = useShopBrand()
const showModal = ref(false)

// Shop name fallback chain: WL brand → "this site" so the title never reads
// awkwardly even if the brand bootstrap is still pending.
const shopName = computed(() => brand.value?.shopName || 'This site')

// On canonical Zaveato, the privacy policy is at /privacy-policy. On a WL
// host we route to the shop's About page where Terms / policies live — the
// only legal documents the shop has actually published. Zaveato's own privacy
// policy isn't appropriate on a custom domain because the user isn't on the
// Zaveato brand from their perspective.
const learnMoreHref = computed(() => {
  // WL hosts: clean URL — host implies the shop, so /about is enough.
  if (isWhiteLabel.value) return '/about'
  return '/privacy-policy'
})

onMounted(() => {
  // Load existing consent preferences on mount
  loadPreferences()
})

const handleAcceptAll = async () => {
  await acceptAll()
  console.log('User accepted all cookies')
}

const handleRejectAll = async () => {
  await rejectAll()
  console.log('User rejected non-essential cookies')
}

const showPreferences = () => {
  showModal.value = true
}

const handleSavePreferences = (preferences: CookiePreferences) => {
  console.log('User saved custom preferences:', preferences)
}
</script>

<style scoped>
/* Additional responsive styles if needed */
@media (max-width: 640px) {
  button {
    width: 100%;
    justify-content: center;
  }
}
</style>
